import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { EmptyState, Loader, NeedsHousehold, PageHeader, useLoad, useNotify, useRun } from '../common';
import { nearDay, quantity } from '../labels';

const LEVELS: { value: T.StockLevel; text: string }[] = [
  { value: 'Full', text: 'Cheio' },
  { value: 'Half', text: 'Meio' },
  { value: 'Low', text: 'Pouco' },
  { value: 'Out', text: 'Acabou' },
];

const ALL = 'all';
const NO_STORE = 'none';

/** "contado há 20 dias · 2 em casa · a semana gasta 3", as Android's PrepareWeekItem.Detail. */
function detail(item: T.PrepareWeekItem): string {
  const s = item.stock;
  const days = s.stockUpdatedAt ? Math.floor((Date.now() - new Date(s.stockUpdatedAt).getTime()) / 86400000) : null;
  const parts = [item.reason === 'NearMinimum'
    ? `perto do mínimo (${quantity(s.minStock, s.unit)})`
    : item.reason === 'RunsOut' && s.runsOutOn
    ? `acaba ${nearDay(s.runsOutOn)}, antes da ida`
    : days !== null ? `contado há ${days} dias` : 'nunca contado'];
  if (s.quantity !== null && s.quantity !== undefined) parts.push(`${quantity(s.quantity, s.unit)} em casa`);
  if (s.upcoming > 0) parts.push(`a semana gasta ${quantity(s.upcoming, s.unit)}`);
  return parts.join(' · ');
}

/**
 * "Preparar a semana": the items whose count is old or near the minimum, set by eye before the shopping.
 * Pouco and Acabou put the item on the list.
 */
const PrepareWeekPage: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const run = useRun();
  const { data, loading, error, reload } = useLoad(api.getPrepareWeek);
  const [store, setStore] = useState<string>(ALL);
  /** Levels chosen here: kept when the filter changes. */
  const [chosen, setChosen] = useState<Record<string, { level: T.StockLevel; stock: T.StockItem }>>({});

  const items = (data?.items ?? []).filter(i =>
    store === ALL ? true : store === NO_STORE ? !i.stock.storePublicId : i.stock.storePublicId === store);
  const withoutStore = (data?.items ?? []).filter(i => !i.stock.storePublicId).length;

  const choose = async (item: T.PrepareWeekItem, level: T.StockLevel | null) => {
    if (!level) return;
    const saved = await run(() => api.setStockLevel(item.stock.catalogItemPublicId, level));
    if (!saved) return;
    setChosen(c => ({ ...c, [item.stock.catalogItemPublicId]: { level, stock: saved } }));
    const text = LEVELS.find(l => l.value === level)!.text.toLowerCase();
    notify(`${item.stock.name}: ${text} · ${quantity(saved.quantity, saved.unit)}${saved.isOnList ? ' · na lista' : ''}`);
  };

  const done = () => {
    notify(Object.keys(chosen).length === 0 ? 'Feito' : 'Feito · lista atualizada', { severity: 'success' });
    navigate('/casa/semana');
  };

  return (
    <>
      <PageHeader
        eyebrow="Em casa"
        title="Preparar a semana"
        subtitle="Vê como está cada um e escolhe o nível. Pouco e Acabou vão para a lista."
        actions={<Button variant="contained" onClick={done}>Feito</Button>}
      />
      <Loader loading={loading} error={error} onRetry={reload} hasData={data !== undefined}>
        {data && (data.items.length === 0 ? (
          <EmptyState title="Está tudo contado" text="Nada para rever antes das compras." />
        ) : (
          <Stack spacing={1.5}>
            {data.stores.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`Todas ${data.items.length}`} color={store === ALL ? 'primary' : 'default'} onClick={() => setStore(ALL)} />
                {data.stores.map(s => (
                  <Chip key={s.publicId} label={`${s.name} ${s.itemCount}`} color={store === s.publicId ? 'primary' : 'default'} onClick={() => setStore(s.publicId)} />
                ))}
                {withoutStore > 0 && (
                  <Chip label={`Sem loja ${withoutStore}`} color={store === NO_STORE ? 'primary' : 'default'} onClick={() => setStore(NO_STORE)} />
                )}
              </Box>
            )}
            {items.map(item => {
              const picked = chosen[item.stock.catalogItemPublicId];
              const onList = (picked?.stock ?? item.stock).isOnList;
              return (
                <Card key={item.stock.catalogItemPublicId} variant="outlined">
                  <CardContent sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 600 }}>{item.stock.name}</Typography>
                        {item.reason === 'NearMinimum'
                          ? <Chip size="small" color="warning" label="perto do mínimo" />
                          : item.reason === 'RunsOut'
                          ? <Chip size="small" color="warning" label="acaba antes da ida" />
                          : <Chip size="small" label="contagem antiga" />}
                        {onList && <Chip size="small" color="success" label="na lista" />}
                      </Box>
                      <Typography variant="caption" color="text.secondary">{detail(item)}</Typography>
                    </Box>
                    <ToggleButtonGroup
                      exclusive size="small" color="primary"
                      value={picked?.level ?? null}
                      onChange={(_, level) => choose(item, level)}
                      aria-label={`Nível de ${item.stock.name}`}
                    >
                      {LEVELS.map(l => <ToggleButton key={l.value} value={l.value} sx={{ px: 1.5 }}>{l.text}</ToggleButton>)}
                    </ToggleButtonGroup>
                  </CardContent>
                </Card>
              );
            })}
            <Box><Button variant="contained" onClick={done}>Feito</Button></Box>
          </Stack>
        ))}
      </Loader>
    </>
  );
};

const PrepareWeekRoute: React.FC = () => <NeedsHousehold><PrepareWeekPage /></NeedsHousehold>;
export default PrepareWeekRoute;
